const defaultScreens = [320, 640, 768, 1024, 1280, 1536];

export type SrcSetConfig = {
  loader: (options: { width: number }) => string;
  widths: (number | null | ((screenWidth: number) => number | null))[];
  screens?: number[];
};

// Generate a "srcset" attribute for an image.
// If a width is a function, it should run for every screen width. Null values will be ignored.
// Every width will be multiplied for 2 to account for retina devices.
export const buildSrcSet = (config: SrcSetConfig) => {
  const { loader, widths, screens = defaultScreens } = config;

  const w = widths
    .flatMap((width) => {
      if (typeof width === 'function') {
        return screens.map((breakpoint) => width(breakpoint));
      }

      return [width];
    })
    .filter(Boolean) as number[];
  // add retina sizes, remove duplicates and sort
  const allW = [...new Set([...w, ...w.map((width) => width * 2)])].sort((a, b) => a - b);

  return allW
    .map((w) => {
      const width = Math.round(w);

      return `${loader({ width })} ${width}w`;
    })
    .join(', ');
};

const defaultBreakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
} as const;

export type SizesConfig<T extends string | number | symbol> = { default: string } & Partial<
  Record<T, string>
>;

export const buildSizes = <T extends Record<string, number> = typeof defaultBreakpoints>(
  sizes: SizesConfig<keyof T>,
  breakpoints?: T
) => {
  const { default: defaultSize, ...rest } = sizes;
  const bk = (breakpoints || defaultBreakpoints) as T;

  const sizesArr = Object.entries(rest).reduce<string[]>((acc, [key, value]) => {
    acc.push(`(max-width: ${bk[key]}px) ${value}`);

    return acc;
  }, []);

  return sizesArr.concat(defaultSize).join(', ');
};
